import {type ReactNode} from 'react';
import {Pie, PieChart} from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent
} from '@kroll-resolver-crisp/ui-components-react';
import {ChartCard} from './ChartCard';
import {distributionSlices, type Slice} from './distribution';
import {formatNumber} from './format';
import {type ReportDistributionBlock} from '@/api/reporting';

// The pie and the linear bar are two drawings of the same slices: `block.type` picks one.
export function DistributionBlock({
  block,
  actions,
  collapsible
}: {
  readonly block: ReportDistributionBlock;
  readonly actions?: ReactNode;
  readonly collapsible?: boolean | undefined;
}) {
  const slices = distributionSlices(block);

  return (
    <ChartCard
      block={block}
      actions={actions}
      collapsible={collapsible}
      empty={slices.length === 0}>
      {block.type === 'linear_bar_chart' ? (
        <LinearBar block={block} slices={slices} />
      ) : (
        <Pied block={block} slices={slices} />
      )}
    </ChartCard>
  );
}

function Pied({
  block,
  slices
}: {
  readonly block: ReportDistributionBlock;
  readonly slices: Slice[];
}) {
  const config: ChartConfig = Object.fromEntries(
    slices.map((slice) => [slice.key, {label: slice.name, color: slice.color}])
  );

  return (
    <ChartContainer config={config} className="aspect-auto h-72 w-full">
      <PieChart accessibilityLayer>
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideLabel
              nameKey="key"
              formatter={(value, _name, item) => (
                <div className="flex w-full items-center justify-between gap-4">
                  <span className="text-muted-foreground">
                    {config[(item.payload as Slice).key]?.label}
                  </span>
                  <span className="font-mono font-medium tabular-nums">
                    {formatNumber(Number(value), block.format)}
                  </span>
                </div>
              )}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent nameKey="key" />} />
        <Pie
          data={slices.map((slice) => ({...slice, fill: slice.color}))}
          dataKey="value"
          nameKey="key"
          innerRadius={50}
          isAnimationActive={false}
        />
      </PieChart>
    </ChartContainer>
  );
}

// One strip split in proportion, with the legend doubling as the readout: name, value and share.
// Segments are plain boxes rather than a chart, so the exact figures are always on screen.
function LinearBar({
  block,
  slices
}: {
  readonly block: ReportDistributionBlock;
  readonly slices: Slice[];
}) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <div className="space-y-4">
      <div className="flex h-6 w-full overflow-hidden" role="img" aria-label={block.title}>
        {slices.map((slice) => (
          <div
            key={slice.key}
            title={`${slice.name}: ${formatNumber(slice.value, block.format)}`}
            style={{width: `${(slice.value / total) * 100}%`, backgroundColor: slice.color}}
          />
        ))}
      </div>
      <ul className="grid gap-2 text-sm sm:grid-cols-2">
        {slices.map((slice) => (
          <li key={slice.key} className="flex items-center gap-2">
            <span className="size-2.5 shrink-0" style={{backgroundColor: slice.color}} />
            <span className="min-w-0 flex-1 truncate">{slice.name}</span>
            <span className="text-muted-foreground tabular-nums">
              {formatNumber(slice.value, block.format)} ·{' '}
              {formatNumber(slice.value / total, 'percent')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
